// Error handling utilities for analytics dashboard
export class AnalyticsErrorHandler {
  constructor() {
    this.retryAttempts = new Map()
    this.maxRetries = 3
    this.retryDelay = 1000 // 1 second base delay
  }

  // Handle chart rendering errors
  handleChartError(error, chartController) {
    console.error('Chart rendering error:', error)

    const errorType = this.categorizeError(error)
    const fallbackAction = this.getFallbackAction(errorType)

    // Show appropriate error message
    const errorMessage = this.getErrorMessage(errorType)
    chartController.showError(errorMessage)

    // Attempt fallback if available
    if (fallbackAction) {
      fallbackAction(chartController)
    }

    // Track error for analytics
    this.trackError('chart_render', error, {
      chartType: chartController.typeValue,
      sectionType: chartController.sectionTypeValue
    })
  }

  // Handle data loading errors
  handleDataError(error, controller, retryCallback) {
    console.error('Data loading error:', error)

    const errorType = this.categorizeError(error)
    const canRetry = this.canRetry(controller.element.id, errorType)

    const errorMessage = this.getErrorMessage(errorType)
    controller.showError(errorMessage)

    // Offer retry if appropriate
    if (canRetry && retryCallback) {
      this.scheduleRetry(controller.element.id, retryCallback, errorType)
    }

    // Track error for analytics
    this.trackError('data_load', error, {
      controllerId: controller.element.id,
      canRetry
    })
  }

  // Handle browser compatibility issues
  handleCompatibilityError(feature, controller) {
    console.warn(`Browser compatibility issue: ${feature} not supported`)

    const fallbackMessage = this.getCompatibilityFallback(feature)
    controller.showError(fallbackMessage)

    // Show simplified view if available
    if (controller.showSimplifiedView) {
      controller.showSimplifiedView()
    }

    // Track compatibility issue
    this.trackError('compatibility', new Error(`${feature} not supported`), {
      userAgent: navigator.userAgent,
      feature,
      browserVersion: this.getBrowserVersion(),
      supportedFeatures: this.getSupportedFeatures()
    })
  }

  // Categorize error types
  categorizeError(error) {
    if (!error) return 'unknown'

    const message = error.message?.toLowerCase() || ''

    if (message.includes('network') || message.includes('fetch')) {
      return 'network'
    } else if (message.includes('chart') || message.includes('canvas')) {
      return 'chart_render'
    } else if (message.includes('data') || message.includes('parse')) {
      return 'data_format'
    } else if (message.includes('permission') || message.includes('unauthorized')) {
      return 'permission'
    } else if (error.name === 'TypeError') {
      return 'browser_compatibility'
    } else {
      return 'unknown'
    }
  }

  // Get user-friendly error messages
  getErrorMessage(errorType) {
    const messages = {
      network: 'Unable to connect to the server. Please check your internet connection and try again.',
      chart_render: 'Unable to display chart. Your browser may not support this feature.',
      data_format: 'The data format is invalid. Please refresh the page and try again.',
      permission: 'You don\'t have permission to access this data.',
      browser_compatibility: 'This feature is not supported in your browser. Please try using a modern browser.',
      unknown: 'An unexpected error occurred. Please refresh the page and try again.'
    }

    return messages[errorType] || messages.unknown
  }

  // Get fallback actions for different error types
  getFallbackAction(errorType) {
    const fallbacks = {
      chart_render: (controller) => {
        // Show tabular data instead of chart
        controller.showTableFallback?.()
      },
      browser_compatibility: (controller) => {
        // Show simplified version
        controller.showSimplifiedView?.()
      },
      data_format: (controller) => {
        // Show empty state with explanation
        controller.showEmptyState?.('Invalid data format')
      },
      network: (controller) => {
        // Show offline mode if available
        controller.showOfflineMode?.()
      }
    }

    return fallbacks[errorType]
  }

  // Check if retry is allowed
  canRetry(elementId, errorType) {
    const retryableErrors = ['network', 'data_format', 'unknown']
    if (!retryableErrors.includes(errorType)) {
      return false
    }

    const attempts = this.retryAttempts.get(elementId) || 0
    return attempts < this.maxRetries
  }

  // Schedule retry with exponential backoff
  scheduleRetry(elementId, retryCallback, errorType) {
    const attempts = this.retryAttempts.get(elementId) || 0
    const delay = this.retryDelay * Math.pow(2, attempts)

    setTimeout(() => {
      this.retryAttempts.set(elementId, attempts + 1)
      retryCallback()
    }, delay)
  }

  // Reset retry counter
  resetRetries(elementId) {
    this.retryAttempts.delete(elementId)
  }

  // Get browser compatibility fallback messages
  getCompatibilityFallback(feature) {
    const fallbacks = {
      'Chart.js': 'Charts are not available in your browser. Please upgrade to a modern browser to view visual analytics.',
      'Canvas': 'Visual charts require a modern browser. Please upgrade your browser to view charts.',
      'Fetch API': 'Your browser doesn\'t support modern web features. Please upgrade to view live data.',
      'CSS Grid': 'The dashboard layout may not display correctly in your browser. Please upgrade for the best experience.',
      'ES6': 'Your browser doesn\'t support modern JavaScript features. Please upgrade for full functionality.',
      'WebGL': 'Advanced chart features are not available. Basic charts will be shown instead.',
      'LocalStorage': 'Settings cannot be saved in your browser. Please upgrade for a better experience.'
    }

    return fallbacks[feature] || 'This feature requires a modern browser. Please upgrade for the best experience.'
  }

  // Track errors for monitoring (can be extended to send to analytics service)
  trackError(type, error, context = {}) {
    const errorData = {
      type,
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      context
    }

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.group(`Analytics Error: ${type}`)
      console.error('Error:', error)
      console.log('Context:', context)
      console.groupEnd()
    }

    // In production, you might want to send this to an error tracking service
    // Example: Sentry.captureException(error, { extra: errorData })
  }

  // Check browser compatibility
  checkBrowserCompatibility() {
    const compatibility = {
      canvas: !!document.createElement('canvas').getContext,
      fetch: typeof fetch !== 'undefined',
      es6: typeof Symbol !== 'undefined',
      cssGrid: CSS && CSS.supports && CSS.supports('display', 'grid'),
      // Note: chartjs is lazily loaded, so we don't check it here
      webgl: this.checkWebGLSupport(),
      localStorage: this.checkLocalStorageSupport(),
      promises: typeof Promise !== 'undefined',
      intersectionObserver: typeof IntersectionObserver !== 'undefined'
    }

    const unsupported = Object.entries(compatibility)
      .filter(([_, supported]) => !supported)
      .map(([feature]) => feature)

    return {
      isCompatible: unsupported.length === 0,
      unsupportedFeatures: unsupported,
      compatibility
    }
  }

  // Check WebGL support for advanced chart features
  checkWebGLSupport() {
    try {
      const canvas = document.createElement('canvas')
      return !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
    } catch (e) {
      return false
    }
  }

  // Check localStorage support
  checkLocalStorageSupport() {
    try {
      const test = 'test'
      localStorage.setItem(test, test)
      localStorage.removeItem(test)
      return true
    } catch (e) {
      return false
    }
  }

  // Get browser version information
  getBrowserVersion() {
    const userAgent = navigator.userAgent
    let browserName = 'Unknown'
    let browserVersion = 'Unknown'

    if (userAgent.indexOf('Chrome') > -1) {
      browserName = 'Chrome'
      browserVersion = userAgent.match(/Chrome\/([0-9.]+)/)?.[1] || 'Unknown'
    } else if (userAgent.indexOf('Firefox') > -1) {
      browserName = 'Firefox'
      browserVersion = userAgent.match(/Firefox\/([0-9.]+)/)?.[1] || 'Unknown'
    } else if (userAgent.indexOf('Safari') > -1) {
      browserName = 'Safari'
      browserVersion = userAgent.match(/Version\/([0-9.]+)/)?.[1] || 'Unknown'
    } else if (userAgent.indexOf('Edge') > -1) {
      browserName = 'Edge'
      browserVersion = userAgent.match(/Edge\/([0-9.]+)/)?.[1] || 'Unknown'
    }

    return { name: browserName, version: browserVersion }
  }

  // Get list of supported features
  getSupportedFeatures() {
    const compatibility = this.checkBrowserCompatibility()
    return Object.entries(compatibility.compatibility)
      .filter(([_, supported]) => supported)
      .map(([feature]) => feature)
  }

  // Show browser compatibility warning
  showCompatibilityWarning(unsupportedFeatures) {
    // Don't show multiple warnings
    if (document.querySelector('.browser-compatibility-warning')) {
      return
    }

    const warningElement = document.createElement('div')
    warningElement.className = 'browser-compatibility-warning bg-warning-container text-on-warning-container p-4 rounded-lg mb-4'
    warningElement.innerHTML = `
      <div class="flex items-start gap-3">
        <div class="text-warning text-xl">⚠️</div>
        <div class="flex-1">
          <h3 class="font-semibold mb-2">Browser Compatibility Notice</h3>
          <p class="text-sm mb-2">
            Some features may not work properly in your browser.
            For the best experience, please upgrade to a modern browser.
          </p>
          <details class="text-xs">
            <summary class="cursor-pointer">Technical Details</summary>
            <div class="mt-2">
              <p class="mb-2">Unsupported features:</p>
              <ul class="list-disc list-inside space-y-1">
                ${unsupportedFeatures.map(feature => `<li>${feature} not supported</li>`).join('')}
              </ul>
              <p class="mt-2">Browser: ${navigator.userAgent}</p>
            </div>
          </details>
          <div class="mt-3 flex gap-2">
            <button type="button"
                    class="px-3 py-1 bg-warning text-on-warning rounded text-xs font-medium hover:bg-warning-hover transition-colors"
                    onclick="this.closest('.browser-compatibility-warning').style.display='none'">
              Dismiss
            </button>
            <a href="https://browsehappy.com/"
               target="_blank"
               class="px-3 py-1 bg-primary text-on-primary rounded text-xs font-medium hover:bg-primary-hover transition-colors">
              Upgrade Browser
            </a>
          </div>
        </div>
      </div>
    `

    // Insert at the top of the dashboard
    const dashboard = document.querySelector('.dashboard-container') || document.body
    if (dashboard) {
      dashboard.insertBefore(warningElement, dashboard.firstChild)
    }
  }
}

// Export singleton instance
export const errorHandler = new AnalyticsErrorHandler()

// Browser compatibility check utility
export function checkAndWarnBrowserCompatibility() {
  const { isCompatible, unsupportedFeatures } = errorHandler.checkBrowserCompatibility()

  if (!isCompatible) {
    errorHandler.showCompatibilityWarning(unsupportedFeatures)
  }

  return isCompatible
}

// Enhanced error recovery with circuit breaker pattern
export class ErrorRecovery {
  constructor() {
    this.circuitBreakers = new Map()
    this.offlineMode = false
    this.setupOfflineDetection()
  }

  // Setup offline/online detection
  setupOfflineDetection() {
    window.addEventListener('online', () => {
      this.offlineMode = false
      this.dispatchEvent('analytics:connectionRestored')
    })

    window.addEventListener('offline', () => {
      this.offlineMode = true
      this.dispatchEvent('analytics:connectionLost')
    })

    // Initial check
    this.offlineMode = !navigator.onLine
  }

  // Circuit breaker pattern for failing services
  withCircuitBreaker(serviceId, fn, fallback, options = {}) {
    const { threshold = 5, timeout = 60000, resetTimeout = 300000 } = options

    if (!this.circuitBreakers.has(serviceId)) {
      this.circuitBreakers.set(serviceId, {
        failures: 0,
        lastFailure: null,
        state: 'closed' // closed, open, half-open
      })
    }

    const breaker = this.circuitBreakers.get(serviceId)

    // If circuit is open, check if we should try again
    if (breaker.state === 'open') {
      if (Date.now() - breaker.lastFailure > resetTimeout) {
        breaker.state = 'half-open'
      } else {
        console.warn(`Circuit breaker open for ${serviceId}, using fallback`)
        return fallback ? fallback() : null
      }
    }

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.recordFailure(serviceId, new Error('Timeout'))
        reject(new Error('Service timeout'))
      }, timeout)

      try {
        const result = fn()

        if (result && typeof result.then === 'function') {
          result
            .then(value => {
              clearTimeout(timeoutId)
              this.recordSuccess(serviceId)
              resolve(value)
            })
            .catch(error => {
              clearTimeout(timeoutId)
              this.recordFailure(serviceId, error)
              if (fallback) {
                resolve(fallback())
              } else {
                reject(error)
              }
            })
        } else {
          clearTimeout(timeoutId)
          this.recordSuccess(serviceId)
          resolve(result)
        }
      } catch (error) {
        clearTimeout(timeoutId)
        this.recordFailure(serviceId, error)
        if (fallback) {
          resolve(fallback())
        } else {
          reject(error)
        }
      }
    })
  }

  recordSuccess(serviceId) {
    const breaker = this.circuitBreakers.get(serviceId)
    if (breaker) {
      breaker.failures = 0
      breaker.state = 'closed'
    }
  }

  recordFailure(serviceId, error) {
    const breaker = this.circuitBreakers.get(serviceId)
    if (breaker) {
      breaker.failures++
      breaker.lastFailure = Date.now()

      if (breaker.failures >= 5) { // threshold
        breaker.state = 'open'
        console.warn(`Circuit breaker opened for ${serviceId} due to repeated failures`)
      }
    }
  }

  dispatchEvent(eventName, detail = {}) {
    const event = new CustomEvent(eventName, { detail, bubbles: true })
    document.dispatchEvent(event)
  }

  isOffline() {
    return this.offlineMode
  }
}

// Create singleton instance
export const errorRecovery = new ErrorRecovery()

// Graceful degradation helper
export function withGracefulDegradation(fn, fallback, errorMessage = null) {
  return function(...args) {
    try {
      return fn.apply(this, args)
    } catch (error) {
      console.warn('Graceful degradation:', error)
      if (errorMessage) {
        console.warn(errorMessage)
      }
      return fallback ? fallback.apply(this, args) : null
    }
  }
}

// Enhanced graceful degradation with retry logic
export function withRetryAndFallback(fn, fallback, options = {}) {
  const { maxRetries = 3, retryDelay = 1000, exponentialBackoff = true } = options

  return async function(...args) {
    let lastError = null

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fn.apply(this, args)
      } catch (error) {
        lastError = error

        if (attempt < maxRetries) {
          const delay = exponentialBackoff
            ? retryDelay * Math.pow(2, attempt)
            : retryDelay

          console.warn(`Attempt ${attempt + 1} failed, retrying in ${delay}ms:`, error)
          await new Promise(resolve => setTimeout(resolve, delay))
        }
      }
    }

    console.warn('All retry attempts failed, using fallback:', lastError)
    return fallback ? fallback.apply(this, args) : null
  }
}
